import { not, or, createMachine } from 'xstate';

import { routePath } from 'config/route-config';
import { actionUrl } from 'config/static-content';
import { events } from 'machines/config/events';
import { guards } from 'machines/config/guards';
import { stringToBool } from 'utils/misc';

export const moveInMachineConfig = {
  id: 'moveInMachine',
  context: {
    guards: {}
  },
  initial: 'home',
  states: {
    home: {
      on: {
        NEXT: {
          target: 'findAddress'
        },
        BACK: {
          target: 'externalBgHomeMove'
        },
        BLOCK_JOURNEY: {
          target: 'businessUpdatesAnnouncement'
        }
      },

      meta: {
        path: routePath.moveIn
      }
    },

    businessUpdatesAnnouncement: {
      on: {
        NEXT: {
          target: 'businessUpdatesSubmitEmail'
        },
        ALLOW_JOURNEY: {
          target: 'home'
        }
      },

      meta: {
        path: routePath.businessUpdatesAnnouncement
      }
    },
    businessUpdatesSubmitEmail: {
      on: {
        NEXT: {
          target: 'businessUpdatesSubmitEmailSuccess'
        }
      },
      meta: {
        path: routePath.businessUpdatesSubmitEmail
      }
    },

    businessUpdatesSubmitEmailSuccess: {
      meta: {
        path: routePath.businessUpdatesSubmitEmailSuccess
      }
    },

    findAddress: {
      on: {
        NEXT: [
          {
            target: 'exceptions',
            description: "not('isMoveInEligible')",
            guard: not('isMoveInEligible')
          },
          {
            target: 'splitSupply',
            guard: 'isNepSplitSupply'
          },
          {
            target: 'classicSupply',
            description: "or(['isBGCustomer', 'isSapSplitSupply'])",
            guard: or(['isBGCustomer', 'isSapSplitSupply'])
          },
          {
            target: 'nonSupply',
            guard: 'isNewToBrand'
          },
          {
            target: 'moveInDate'
          }
        ],
        LOGIN: {
          target: 'externalLogin'
        }
      },
      meta: {
        path: routePath.findAddress
      }
    },
    splitSupply: {
      on: {
        NEXT: {
          target: 'moveInDate'
        }
      },
      meta: {
        path: routePath.splitSupply
      }
    },
    classicSupply: {
      on: {
        NEXT: {
          target: 'externalBgClassicHomeMove'
        }
      },
      meta: {
        path: routePath.classicSupply
      }
    },
    nonSupply: {
      on: {
        NEXT: {
          target: 'externalDirectSales'
        }
      },
      meta: {
        path: routePath.nonSupply
      }
    },
    moveInDate: {
      on: {
        NEXT: [
          {
            target: 'exceptions',
            description: "not('isMoveInEligible')",
            guard: not('isMoveInEligible')
          },
          {
            target: 'newHome'
          }
        ]
      },
      meta: {
        path: routePath.moveDate
      }
    },
    exceptions: {
      on: {
        CHAT: {
          target: 'externalContactUs'
        }
      },
      meta: {
        path: routePath.exceptions
      }
    },

    newHome: {
      on: {
        NEXT: {
          target: 'consumptionEntry'
        }
      },
      meta: {
        path: routePath.newHome
      }
    },

    consumptionEntry: {
      on: {
        NEXT: [
          {
            target: 'quote'
          }
        ]
      },
      meta: {
        path: routePath.consumptionEntry
      }
    },

    quote: {
      on: {
        NEXT: {
          target: 'createAccount'
        }
      },
      meta: {
        path: routePath.quote
      }
    },

    createAccount: {
      on: {
        NEXT: [
          {
            target: 'directDebit',
            guard: 'isAnyDirectDebit'
          },
          {
            target: 'meterReads'
          }
        ],
        CHANGE_ADDRESS: { target: 'findAddress' }
      },
      meta: {
        path: routePath.createAccount
      }
    },

    directDebit: {
      on: {
        NEXT: {
          target: 'meterReads'
        }
      },
      meta: {
        path: routePath.directDebit
      }
    },

    meterReads: {
      on: {
        NEXT: {
          target: 'psrEntry'
        },
        CHAT: {
          target: 'externalContactUs'
        }
      },
      meta: {
        path: routePath.meterReads
      }
    },

    psrEntry: {
      on: {
        NEXT: [
          {
            target: 'userPreferences'
          }
        ]
      },
      meta: {
        path: routePath.psrEntry
      }
    },

    userPreferences: {
      on: {
        NEXT: {
          target: 'orderSummary'
        }
      },
      meta: {
        path: routePath.userPreferences
      }
    },

    orderSummary: {
      on: {
        NEXT: {
          target: 'orderComplete'
        }
      },
      meta: {
        path: routePath.orderSummary
      }
    },
    orderComplete: {
      on: {
        DIRECT_SALES: {
          target: 'externalDirectSales'
        }
      },
      meta: {
        path: routePath.orderComplete
      }
    },
    externalLogin: {
      meta: {
        path: actionUrl.login
      }
    },
    externalContactUs: {
      meta: { path: actionUrl.contact }
    },
    externalBgClassicHomeMove: {
      meta: { path: actionUrl.bgClassicHomeMoveUrl }
    },
    externalDirectSales: {
      meta: { path: actionUrl.directSales }
    },
    externalBgHomeMove: {
      meta: { path: actionUrl.bgHomeMove }
    }
  },
  ...events
};

const { VITE_XSTATE_DEBUG } = process.env;
const debug = stringToBool(VITE_XSTATE_DEBUG);
// for VS extesnion
/* istanbul ignore next -- @preserve */
debug &&
  createMachine(
    {
      /** @xstate-layout N4IgpgJg5mDOIC5QFsD2A3MBJAdgWQEMBjACwEscwA6E1ZMAYgDkBRADQBUBtABgF1EoAA6pYZAC5lUOQSAAeiAEwA2AKxUAnAGYAHDwDsPACxqeyxVoA0IAJ6IN+qqv0BGZS7fHdR7QF9f1miYuISkFNS09AwAQgAyAPIAwgDSAPoAUvEAqgBKrACavAJIICJiktKyCgiqylTK7qoaGo3mivqK1nYIPopUPC6qxi5GLho8PDr+gRjY+MTklFQARgCuYpSwsFlCEATicACCODioqzhEYPQ44szs3PyyZRJSMiXV+k1ULooWyvrNH76T5dRCGPr6HRqFzGHSKHgOFzTEBBOahRbUNYbODbXb7I4nM4XK5gG4MQ6xBIAdQy2TyLEKjxKzwqb1A1S0Ri03xUn1UOh0HhhOlUoJ6Wj6Jlq2jc-L0IuRqJCC3CK3W4S2Oz2B1gx1O50u11uRSeohelXeiAAtIodFQjA6hjpdC4tDxVFpVEYxa5uaNBjDOd5FEiAijZsqwkssRrcdq4ABlVbLZASFjIAhkAA2d04JuZZtZVUQPx0Riofy0kLUzn+Oh9PG5tuhsPhiMVEfmUeoADMKBBDhAIAAnHG5h7FYSF17FhDA8ufCaQniKWrmfRiq2e-q1N0+CVGNfKDvBLsYqh9nADoejrbjrguSelacW9lgrRNqFlsz6Q-SsUhi49RNKuZiugiGjOieaIqksl7XiOY6sHmihPiyM6WnOJiaBoDr-IYxjKAYYpaA0-SESuWgaKWJjHmGSpnqq8GDohd7IdwWhoS+bLyGCozfMoDqukYAyuhKYpGFC3xuqoqg-IMyi4ao0GRuezE3kh9xcEYXHlBhb5zopVCeoe8ITDowLumK-I8FQHSyXJ7pei0igqYxcH9ixt6wAwCQAOJYEw+ZTnpr68QgygWRWAJci0zr+puzo7soe7aIoh4NPobnoqqsBCFmEhJkI+U2PeTIheaPHVJ4fRUe4mWOpy3q2IgRirlQgqQSGkmCZCUz0Z2OVLEQWYEFsZBEEVJVlbplWzh4Gh9E0IkNM0v4rhuLUIKRdQTCYlHUbatHZbB1CnDgU1ZqV7HBc+oVVSWYxLbhZiKQ4IntBJi39IMwyjOMkwGCd3ZUAxAAi+Izaa93zb+GhOGWv7OPonLuvWW1GPodSyRZwINJyOgOMD57g5DN2PtDc2YS4SMdYMIYSmobXiVtzjqOYtQTPCYwSsTqpgHIlxCKyPmJAAEocE6U0W1NuEBpEeu4cI0yuzXdFaIzwxMGgeoTnyo2YfNLJQADuYt0IwN3lXdVMGSGDgdc6KUOZJWOihj7g8sCIYTJzx0DaeQ1nWAZsW-eFMFjDmH2+oHTupJgFOp0rOfPUq5ESuAxpaGMyB6dVDDviHA2EIltadb6FhdUKjunZVYwkRcoNMoEme+ugntBKbi-HRucwSDcCSBmrJMKsyDLGAw7xD20SQMOqB0D5VuzTLduuPLPXuBYWdq4gzjlooi0-IpvLUVoRvUIPZDD68o-j5P08AApgKg+Vl3mFfcbObpGYKUINJjCycJrK-grEfFQi1-hnwvlQAAjqsVABwoaR1tuFRmdp7K-EIm6AYVgU4H3ASfKBroYHiAIMOMgPYexgzAGQ7MS9y4r30mguSQFhJukWgKAUDgQEEJDBA0+JCA793PEQUc+JDhECIESW4y9pbMI5Lje0O1IqH0UvyaykU7IwlUFg9onxBIwLEWACRUiZEMHFocJgfkWCpEOGDMGOQWAJgTLdSuD1trSg6kMQwwJPTjFImKEYQEYpwjagKdwLgywwIgGQUcRBxA0OWBIZBFVV7hX9OoLkApXAqBSs0TRGCdF6OBLUIwMD6AHGHDkExEBUk23SdULkcl6htR8M0BWKhCnaPdCUgx5ThGqVVEIWAw4WA3GHNdRh8iq6tWcLZAEAx0q-l-C4CSXptHtDhH4v4KgYEjLGRMqZeYI5pIUXMwwdlxg-ExrctZKd1A-C9ERGuNze7hjziDA5iQCDC1WKOep7jZy9D9D8SCMp4TJ26IeBcQJtko12e8hiQcqDrEno-UcPZJ6kkuAwj+TDZk1DMhWBEFl-FUUbJoxwuMolQlwszHOHyRGqlQMOCAk8kzIAzJMwFX9MLPPULaFWLz1p4O6LUalwJaWKQdBYUMYZTjsvgCUZFp0ZkeKtFje0jpJguhkp6Tcno+iLQMHCD8h0GgwMiGAdV80RgVjCUArG6U1Aty2g7CwOs+TOzkilGBMZNhxnxLqQkBoSQ3FtZhB0jg2otH+KjP2DZjVekxilEYll+p9yGdGdUgatTBqTCmNMGZsyRoMjrPoHgHR6ABJAza3RwSaBTVjLQ6bDAaH9bmnE+adSFtTOIdMmYsxJikTiMt4URghlaWMAY+89BQrBCuJt0a02Y0MJmpl2beyeQ0lscdTTsKtqaL7JoFkmhBJhPUBwFgPychPrUGBeUCriEut0M5hLfrlhVm4TGzgoltQAh4Yy7RurOB7oJJFg184jTGmISaqxipXX3SWFcWtr0o3sq9Eiah6j2XxkMQ8Qis3uTOtIV9yGEAwh8P0dKtQyztBeejaF1F6jOl9lWQUv5IOfJJp2CGBwKMKXUI2IiCc6NOhAYK7QHcuQwsZaqgegswDC1eMq99HiPAtMJrJAEh9UbmE3GMeoVEmhuBRh+QmhtBkkaoKbc29BBOQQwbaBwZZmZrVbgsnWW8TBHw7dZlFhcDjF1LhR9KAoeRrWojCLGTHWpESubWCUugGbOBgVfG+0g74TynjPOeC9kBqYaechA6VW1pyhG6Dw+iQT4PtBncYAomjOA3Qp88GX9i3zHjlp+L835hcFF+0Yzd+SeDdeK0B+0ESTBFF61rUGQbwMQTalBjTECto6PUDwFlhvae0Lw+r2sZvNchKQ8hlDqG0KHUVoFmEPyuCcP+oVHoabOgO1Nxrs2WtGPEQcSR0jzjiAo-XOowkz1yzet0oBklT0n1bTEuJYAElJIkMDyCtlfiQU+Fj107txVaOh7NulcqKm0MnjUggEAKNtSiRWVt+iESyVGFDyEMPIJw-kwt88BzxniEmdTmFyj5nOv+MYFnZYicc-2aMn5fzRzU8hI4Kiv5Rgo0UmWdZ5YBQGBMAij6uEYFouHBisAWLRzEhu3ygyQwVAkvcA4LmYw4s1C0cYOSui1CekJtx5lSxWXsuHJy7lb7iuEo9I2cibpBRHUBnjvervCMe49PyFoMD-eT0SHQN+AnVsldkq71Z23ajPO6W7+mnuU8+63VQAWVScAECzLEVAUAKAUY5pHlQtE9DK83BYO0s74qQW7h6dLcg68N8zzcYg4gsiW6jgZIU6gbIikEmMHWGito2hw00Zc3Mj5pYC-nWvk969ZmiFARIo1xpEHs2APAsxBMbftLg340opXO4Vrh9Ozsy-n0P4p8fLMMGRHBJBMBvOAR-D0GjCtHWQENqetVqFjSKHBD0SEGmEwfwfwIAA */
      ...moveInMachineConfig
    },
    {
      guards
    }
  );
